// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { Buffer } from 'buffer'

import { ObjectData, TCCService, Template } from '@/services/tcc/configurator'

export type { TemplateFragment } from '@/services/tcc/configurator'
export type { Template } from '@/services/tcc/configurator'

export const EMPTY_TEMPLATE_ID = 'fa6e5a8d-9087-4cd3-80e4-ddb8bc7c2524'

export interface RenderedTemplate {
  id: string
  name: string
  description: string
  services: {
    [key: string]: unknown
  }
}

export const getTemplate = async (templateId: string): Promise<Template> => {
  const response = await TCCService.GetTemplate({ templateId })
  if (!response.template) {
    throw new Error(`Template not found: ${templateId}`)
  }
  return response.template
}

export const getTemplates = async (): Promise<Template[]> => {
  const response = await TCCService.GetTemplates({ deployable: false })
  return response.templates
}

export const fetchTemplate = async (id: string): Promise<RenderedTemplate> => {
  const resp = await TCCService.GetRenderedTemplate({
    templateId: id,
  })

  const d = JSON.parse(new TextDecoder().decode(resp.templateData))
  const tmpl: RenderedTemplate = {
    id: d.id,
    name: d.name,
    description: d.description,
    services: {},
  }

  Object.keys(d.services).forEach((s) => {
    const actions = d.services[s]

    actions.forEach((a: { payload: string }) => {
      a.payload = JSON.parse(
        new TextDecoder().decode(Buffer.from(a.payload, 'base64'))
      )
    })
    tmpl.services[s] = actions
  })

  return tmpl
}

export const submitTemplate = async (f: File): Promise<string> => {
  const b = await f.arrayBuffer()
  const d = JSON.parse(new TextDecoder().decode(b))

  const upload = {
    name: d.name,
    description: d.description,
    services: {} as {
      [key: string]: unknown
    },
  }

  Object.keys(d.services).forEach((s) => {
    const actions: { name: string; payload: unknown }[] = d.services[s]

    upload.services[s] = actions.map(
      (a: { name: string; payload: unknown }) => {
        const jsonStrPayload = JSON.stringify(a.payload)
        const base64Payload = Buffer.from(jsonStrPayload).toString('base64')

        return {
          name: a.name,
          payload: base64Payload,
        }
      }
    )
  })

  const templateData = new TextEncoder().encode(JSON.stringify(upload))

  const resp = await TCCService.UploadTemplate({
    templateData: templateData,
  })

  return resp.templateId
}

export const submitObjectData = async (
  f: File,
  templateId?: string
): Promise<ObjectData[]> => {
  const buf = await f.arrayBuffer()
  const uploadUrlResp = await TCCService.GetObjectDataUploadUrl({
    templateId: templateId,
  })
  /* eslint-disable no-undef */
  await fetch(uploadUrlResp.url, {
    method: 'PUT',
    body: new Uint8Array(buf),
    headers: uploadUrlResp.headers,
  })
  const resp = await TCCService.UploadObjectData({
    templateId: templateId || '',
    referenceId: uploadUrlResp.referenceId,
  })
  return resp.objectData
}

export const deployObjectData = async (
  tenantId: string,
  objectDataIds: string[]
): Promise<ObjectData[]> => {
  const resp = await TCCService.DeployObjectData({
    tenantId: tenantId,
    objectDataIds: objectDataIds,
  })
  return resp.objectDataDeployments
}

export const downloadTemplate = async (template: Template) => {
  const resp = await TCCService.GetRenderedTemplate({
    templateId: template.id,
  })

  const safeTemplateName = (template.name || 'template').replace(
    /[^a-z0-9._-]+/gi,
    '_'
  )

  const bytes = Uint8Array.from(resp.templateData)
  const blob = new Blob([bytes], { type: 'application/json' })
  const downloadUrl = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = `${safeTemplateName}.json`
  document.body.appendChild(link)
  link.click()
  link.parentNode?.removeChild(link)

  URL.revokeObjectURL(downloadUrl)
}

export const downloadObjectDataPackage = async (templateId: string) => {
  TCCService.GetObjectDataPackage({
    templateId: templateId,
  })
    .then((resp) => resp.downloadUrl)
    .then((downloadUrl) => {
      const link = document.createElement('a')
      link.href = downloadUrl
      document.body.appendChild(link)
      link.click()
      link.parentNode?.removeChild(link)
    })
}

export const changeGoldenStatus = async (
  template: Template,
  enabled: boolean
): Promise<boolean> => {
  const resp = await TCCService.UpdateTemplateGoldenStatus({
    templateId: template.id,
    golden: enabled,
  })
  return resp.golden
}

export const deleteTemplate = async (template: Template) => {
  await TCCService.DeleteTemplate({
    templateId: template.id,
  })
}

export const publishTemplate = async (template: Template) => {
  await TCCService.UpdateTemplatePublishedStatus({
    templateId: template.id,
    published: true,
  })
}

export const updateTemplate = async (template: Template) => {
  await TCCService.UpdateTemplate({
    templateId: template.id,
    name: template.name,
    description: template.description,
  })
}
